import { accountAxios } from '@/services/api/accountAxios';
import type {
  ApiEnvelope,
  BillingContact,
  BillingCycle,
  BillingDashboard,
  PaymentMethod,
  RetryPaymentOutcome,
  Subscription,
  SubscriptionPlan,
  TenantInvoice,
} from '@/services/api/accountTypes';

/**
 * Billing surface for the account console.
 *
 * Every route below is guarded server-side by `requireEmployeeRole(OWNER)` —
 * an admin who is not the owner gets a 403. The UI gates on the same fact
 * rather than discovering it through a failed request.
 */
export const accountBillingApi = {
  /** One call powering the persistent account instrument. */
  getDashboard: async (): Promise<BillingDashboard> => {
    const response = await accountAxios.get<ApiEnvelope<BillingDashboard>>('billing/dashboard');
    return response.data.data;
  },

  getSubscription: async (): Promise<Subscription | null> => {
    const response = await accountAxios.get<ApiEnvelope<Subscription>>('billing/subscription');
    return response.data.data ?? null;
  },

  /**
   * Full catalog. `currency` only applies before the org's first paid
   * subscription stamps its billing currency; the backend ignores it after.
   */
  getPlans: async (currency?: string): Promise<SubscriptionPlan[]> => {
    const response = await accountAxios.get<ApiEnvelope<SubscriptionPlan[]>>('subscription-plans', {
      params: currency ? { currency } : undefined,
    });
    return response.data.data ?? [];
  },

  /** Add-ons compatible with the current base plan. */
  getAvailableAddons: async (): Promise<SubscriptionPlan[]> => {
    const response = await accountAxios.get<ApiEnvelope<SubscriptionPlan[]>>('billing/addons/available');
    return response.data.data ?? [];
  },

  // ── Subscription lifecycle ──

  /** Hosted Checkout — used for the first paid subscription. */
  createCheckoutSession: async (input: {
    basePlanId: string;
    billingCycle: BillingCycle;
    addonPlanIds?: string[];
    successUrl: string;
    cancelUrl: string;
    currency?: string;
  }): Promise<string> => {
    const response = await accountAxios.post<ApiEnvelope<{ url: string }>>(
      'billing/checkout-session',
      input
    );
    return response.data.data.url;
  },

  /** Stripe-hosted portal — the fallback for anything not surfaced in-app. */
  createPortalSession: async (returnUrl: string): Promise<string> => {
    const response = await accountAxios.post<ApiEnvelope<{ url: string }>>('billing/portal-session', {
      returnUrl,
    });
    return response.data.data.url;
  },

  /**
   * In-place base-plan change on an existing subscription (prorated).
   * The API takes `toPlanId` and keeps the subscription's existing billing
   * cycle — there is no cycle argument on this route.
   */
  changePlan: async (input: {
    toPlanId: string;
    prorationBehavior?: 'create_prorations' | 'none' | 'always_invoice';
    preserveAddons?: boolean;
  }): Promise<unknown> => {
    const response = await accountAxios.post<ApiEnvelope<unknown>>(
      'billing/subscription/change-plan',
      { preserveAddons: true, ...input }
    );
    return response.data.data;
  },

  addAddon: async (planId: string, quantity?: number): Promise<unknown> => {
    const response = await accountAxios.post<ApiEnvelope<unknown>>(
      `billing/addons/${planId}`,
      quantity ? { quantity } : {}
    );
    return response.data.data;
  },

  /** Takes the SUBSCRIPTION ITEM id, not the plan id. */
  removeAddon: async (itemId: string): Promise<unknown> => {
    const response = await accountAxios.delete<ApiEnvelope<unknown>>(`billing/addons/${itemId}`, {
      data: {},
    });
    return response.data.data;
  },

  cancelSubscription: async (subscriptionId: string, reason?: string): Promise<unknown> => {
    const response = await accountAxios.delete<ApiEnvelope<unknown>>(
      `billing/subscription/${subscriptionId}/cancel`,
      { data: reason ? { reason } : {} }
    );
    return response.data.data;
  },

  // ── Payment methods (Stripe Elements) ──

  getPaymentMethods: async (): Promise<PaymentMethod[]> => {
    const response = await accountAxios.get<ApiEnvelope<PaymentMethod[]>>('billing/payment-methods');
    return response.data.data ?? [];
  },

  /** Client secret for saving a card with Elements — no charge is made. */
  createSetupIntent: async (): Promise<string> => {
    const response = await accountAxios.post<ApiEnvelope<{ clientSecret: string }>>(
      'billing/payment-methods/setup-intent'
    );
    return response.data.data.clientSecret;
  },

  /**
   * Sets the default card. While the subscription is past due the backend also
   * retries the open invoice with the new card and reports the outcome.
   */
  setDefaultPaymentMethod: async (
    paymentMethodId: string
  ): Promise<{ ok: boolean; retriedInvoice: RetryPaymentOutcome | null }> => {
    const response = await accountAxios.post<
      ApiEnvelope<{ ok: boolean; retriedInvoice: RetryPaymentOutcome | null }>
    >(`billing/payment-methods/${paymentMethodId}/default`);
    return response.data.data;
  },

  removePaymentMethod: async (paymentMethodId: string): Promise<void> => {
    await accountAxios.delete(`billing/payment-methods/${paymentMethodId}`);
  },

  /** Dunning recovery. A card decline rejects with HTTP 402. */
  retryPayment: async (paymentMethodId?: string): Promise<RetryPaymentOutcome> => {
    const response = await accountAxios.post<ApiEnvelope<RetryPaymentOutcome>>(
      'billing/subscription/retry-payment',
      paymentMethodId ? { paymentMethodId } : {}
    );
    return response.data.data;
  },

  // ── Invoices & contact ──

  /** Amounts are minor units (cents). */
  getInvoices: async (): Promise<TenantInvoice[]> => {
    const response = await accountAxios.get<ApiEnvelope<TenantInvoice[]>>('billing/invoices');
    return response.data.data ?? [];
  },

  getContact: async (): Promise<BillingContact> => {
    const response = await accountAxios.get<ApiEnvelope<BillingContact>>('billing/contact');
    return response.data.data;
  },

  /** Pass null to send billing email to the organization owner again. */
  updateContact: async (email: string | null): Promise<BillingContact> => {
    const response = await accountAxios.put<ApiEnvelope<BillingContact>>('billing/contact', {
      email,
    });
    return response.data.data;
  },
};
